import React from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowRight, 
  Shield, 
  Sparkles, 
  CheckCircle,
  Camera,
  FileSearch,
  ClipboardCheck,
  Receipt,
  AlertTriangle,
  Clock,
  Eye,
  Zap,
  Target,
  TrendingUp,
  ChevronRight,
  DollarSign,
  XCircle,
  MessageSquare,
  MessageCircle,
  BadgeCheck,
  Wallet,
  HardHat,
  Calculator,
  FileCheck,
  Lightbulb,
  PiggyBank,
  CheckCircle2,
  Lock,
  BookOpen,
  Star,
  X,
  Crown,
  Users,
  FileText,
  BarChart3
} from 'lucide-react';
import { Button } from '../components/ui/button';
import LeonAvatar from '../components/LeonAvatar';

// Animation variants
const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.7, ease: "easeOut" }
};

const fadeInLeft = {
  initial: { opacity: 0, x: -40 },
  animate: { opacity: 1, x: 0 },
  transition: { duration: 0.7, ease: "easeOut" }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.15
    }
  }
};

const scaleIn = {
  initial: { opacity: 0, scale: 0.9 },
  animate: { opacity: 1, scale: 1 },
  transition: { duration: 0.5 }
};

// Les 4 piliers de l'application V1
const leonPowers = [
  {
    icon: FileSearch,
    title: 'Analyser un devis',
    description: 'Vérifiez un devis avant de signer.',
    details: [
      'les postes manquants ou incomplets',
      'les incohérences techniques',
      'les prix anormalement élevés',
      'les oublis fréquents dans les devis',
      'les points de vigilance importants'
    ],
    detailsIntro: 'L\'IA détecte automatiquement :',
    conclusion: 'Vous obtenez un diagnostic clair pour savoir si le devis est fiable.',
    link: '/fonctionnalite/analyse-devis'
  },
  {
    icon: BookOpen,
    title: 'Créer une recette travaux',
    description: 'Guides pas à pas pour vos travaux.',
    details: [
      'les outils nécessaires',
      'les matériaux à acheter',
      'les étapes détaillées',
      'les erreurs à éviter',
      'les conseils de Léon'
    ],
    detailsIntro: 'Chaque recette est structurée étape par étape :',
    conclusion: 'Même un bricoleur débutant peut comprendre rapidement.',
    link: '/fonctionnalite/recettes-travaux'
  },
  {
    icon: Camera,
    title: 'Diagnostic photo',
    description: 'Analysez vos travaux en image.',
    details: [
      'identification du problème',
      'points d\'attention à surveiller',
      'recommandations pratiques',
      'actions à envisager'
    ],
    detailsIntro: 'Envoyez une photo et Léon vous dit :',
    conclusion: 'Une analyse visuelle claire et des conseils personnalisés.',
    link: '/fonctionnalite/diagnostic-photo'
  },
  {
    icon: MessageCircle,
    title: 'Poser une question',
    description: 'Assistant travaux disponible 24h/24.',
    details: [
      'les prix du marché',
      'les techniques de rénovation',
      'les normes et bonnes pratiques',
      'les matériaux adaptés'
    ],
    detailsIntro: 'Léon vous aide à comprendre :',
    conclusion: 'Réponses pédagogiques et simples, même pour un bricoleur du dimanche.',
    link: '/fonctionnalite/assistant'
  }
];

// Common renovation problems - Updated texts
const problems = [
  {
    icon: FileSearch,
    title: 'Devis incompréhensibles',
    description: 'Des termes techniques et des prix flous.',
    solution: 'Léon traduit et analyse chaque ligne pour vous.'
  },
  {
    icon: Wallet,
    title: 'Paiements trop rapides',
    description: 'Vous payez avant que le travail soit terminé.',
    solution: 'Léon valide chaque étape avant paiement.'
  },
  {
    icon: Clock,
    title: 'Questions sans réponse',
    description: 'Vous ne savez pas si le prix est correct.',
    solution: 'Léon analyse et répond à vos questions travaux.'
  }
];

// Images générées - Léon qui salue sur fond noir
const LEON_HERO = "https://static.prod-images.emergentagent.com/jobs/eccefe7e-e8e5-4ae3-a713-2f84c7ec57e8/images/f2bd361aff87ab18a56747c9fa63f0349527e94cc3869c84c8da7aae6d9a9b8c.png";
const CRACKED_WALL_IMAGE = "https://static.prod-images.emergentagent.com/jobs/eccefe7e-e8e5-4ae3-a713-2f84c7ec57e8/images/00e31295a6d12faebfca5c06fec5da725469d28b927fd1c0bad6177d178b0666.png";

// CTA Button Component
function CTAButton({ children, primary = true, to, onClick, icon: Icon = ArrowRight, className = "" }) {
  const navigate = useNavigate();
  
  const handleClick = () => {
    if (onClick) onClick();
    if (to) navigate(to);
  };
  
  if (primary) {
    return (
      <Button
        onClick={handleClick}
        className={`bg-gradient-to-r from-gold-500 to-amber-500 hover:from-gold-400 hover:to-amber-400 text-black font-bold px-8 py-6 text-lg rounded-full shadow-lg shadow-gold-500/25 hover:shadow-gold-500/40 transition-all duration-300 group ${className}`}
        data-testid="cta-primary"
      >
        {children}
        <Icon className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
      </Button>
    );
  }
  
  return (
    <Button
      onClick={handleClick}
      variant="outline"
      className={`border-2 border-gold-500/50 text-gold-400 hover:bg-gold-500/10 hover:border-gold-400 px-8 py-6 text-lg rounded-full transition-all duration-300 ${className}`}
      data-testid="cta-secondary"
    >
      {children}
      {Icon && <Icon className="w-5 h-5 ml-2" />}
    </Button>
  );
}

// Section Component
function Section({ children, className = "", id, dark = false }) {
  return (
    <section 
      id={id}
      className={`py-16 md:py-20 ${dark ? 'bg-zinc-950' : 'bg-zinc-900'} ${className}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {children}
      </div>
    </section>
  );
}

// Section Title Component
function SectionTitle({ overline, title, subtitle, center = true }) {
  return (
    <div className={`mb-12 ${center ? 'text-center' : ''}`}>
      {overline && (
        <motion.p 
          {...fadeInUp}
          className="text-gold-500 font-semibold text-sm uppercase tracking-wider mb-3"
        >
          {overline}
        </motion.p>
      )}
      <motion.h2 
        {...fadeInUp}
        className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-white mb-4"
      >
        {title}
      </motion.h2>
      {subtitle && (
        <motion.p 
          {...fadeInUp}
          className="text-zinc-400 text-lg md:text-xl max-w-3xl mx-auto"
        >
          {subtitle}
        </motion.p>
      )}
    </div>
  );
}

// Floating Card Component for Hero
function FloatingCard({ icon: Icon, title, subtitle, color, delay, position, animationConfig }) {
  return (
    <motion.div 
      initial={{ opacity: 0, scale: 0 }}
      animate={{ 
        opacity: 1, 
        scale: 1,
        y: animationConfig.y,
        x: animationConfig.x,
        rotate: animationConfig.rotate,
      }}
      transition={{ 
        opacity: { duration: 0.3, delay },
        scale: { duration: 0.3, delay },
        y: { duration: animationConfig.yDuration || 1.5, repeat: Infinity, ease: "easeInOut" },
        x: { duration: animationConfig.xDuration || 2, repeat: Infinity, ease: "easeInOut" },
        rotate: { duration: animationConfig.rotateDuration || 1.8, repeat: Infinity, ease: "easeInOut" }
      }}
      whileHover={{ scale: 1.25, zIndex: 50 }}
      className={`absolute ${position} bg-black/90 backdrop-blur-sm border ${color} rounded-xl p-2.5 z-20 cursor-pointer shadow-lg`}
    >
      <div className="flex items-center gap-2">
        <motion.div 
          animate={animationConfig.iconAnimate || { scale: [1, 1.15, 1] }}
          transition={{ duration: 1, repeat: Infinity }}
          className={`w-7 h-7 rounded-lg ${color.replace('border-', 'bg-').replace('/60', '/30')} flex items-center justify-center`}
        >
          <Icon className={`w-4 h-4 ${color.replace('border-', 'text-').replace('/60', '')}`} />
        </motion.div>
        <div>
          <p className="text-white font-bold text-xs">{title}</p>
          <p className={`text-[10px] font-medium ${color.replace('border-', 'text-').replace('/60', '')}`}>{subtitle}</p>
        </div>
      </div>
    </motion.div>
  );
}

// ==================== PRICING SECTION COMPONENT ====================
function PricingSection({ onGetStarted }) {
  const navigate = useNavigate();

  // Plan Gratuit features
  const freeFeatures = [
    { text: '3 analyses de devis offertes', included: true },
    { text: 'Recettes travaux illimitées', included: true },
    { text: 'Diagnostic photo illimité', included: true },
    { text: 'Assistant Léon complet', included: true },
    { text: 'Publicités affichées', included: true, negative: true },
    { text: 'Propositions d\'artisans partenaires', included: true, negative: true },
    { text: 'PDF avec mention publicitaire', included: true, negative: true },
  ];

  // Plan Premium features
  const premiumFeatures = [
    { text: 'Analyses de devis illimitées', included: true, highlight: true },
    { text: 'Recettes travaux illimitées', included: true },
    { text: 'Diagnostic photo illimité', included: true },
    { text: 'Assistant Léon complet', included: true },
    { text: 'Aucune publicité', included: true, highlight: true },
    { text: 'Aucun démarchage commercial', included: true, highlight: true },
    { text: 'PDF sans publicité', included: true, highlight: true },
    { text: 'Traitement prioritaire', included: true, highlight: true },
  ];

  // Social proof stats
  const socialStats = [
    { value: '3 200', label: 'devis analysés', icon: FileText },
    { value: '1 500', label: 'recettes créées', icon: BookOpen },
    { value: '900', label: 'diagnostics photo', icon: Camera },
  ];

  return (
    <Section dark id="tarifs">
      <div className="max-w-5xl mx-auto">
        {/* Title */}
        <SectionTitle 
          overline="Tarification"
          title="Tarifs simples et transparents"
          subtitle="Utilisez Assistant by Léon gratuitement ou passez Premium pour une expérience sans publicité."
        />

        {/* Pricing Cards - 2 columns */}
        <div className="grid md:grid-cols-2 gap-6 lg:gap-8 mb-12 max-w-4xl mx-auto">
          
          {/* Plan Gratuit */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="relative"
          >
            <div className="h-full bg-zinc-800/50 rounded-2xl p-6 lg:p-8 border border-zinc-700 hover:border-zinc-600 transition-all duration-300">
              <div className="mb-6">
                <h3 className="text-xl font-bold text-white mb-2">Gratuit</h3>
                <p className="text-zinc-400 text-sm">Accès complet avec publicités</p>
              </div>

              <div className="mb-6">
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-bold text-white">0 €</span>
                  <span className="text-zinc-400">/ mois</span>
                </div>
                <p className="text-zinc-500 text-sm mt-1">Pour toujours</p>
              </div>

              <ul className="space-y-3 mb-6">
                {freeFeatures.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    {feature.negative ? (
                      <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    ) : (
                      <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    )}
                    <span className={`text-sm ${feature.negative ? 'text-zinc-500' : 'text-zinc-300'}`}>
                      {feature.text}
                    </span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => navigate('/auth')}
                className="w-full py-3 px-6 rounded-xl bg-zinc-700 hover:bg-zinc-600 text-white font-semibold transition-all duration-300"
                data-testid="pricing-free-btn"
              >
                Commencer gratuitement
              </button>
            </div>
          </motion.div>

          {/* Plan Premium - HIGHLIGHTED */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="relative"
          >
            {/* Badge "Recommandé" */}
            <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10">
              <div className="bg-gradient-to-r from-gold-500 to-amber-500 text-black text-xs font-bold px-4 py-1.5 rounded-full flex items-center gap-1.5 shadow-lg shadow-gold-500/25">
                <Star className="w-3.5 h-3.5" fill="currentColor" />
                Recommandé
              </div>
            </div>

            <div className="h-full bg-gradient-to-br from-zinc-800 to-zinc-900 rounded-2xl p-6 lg:p-8 border-2 border-gold-500/50 hover:border-gold-500 transition-all duration-300 shadow-lg shadow-gold-500/10">
              <div className="mb-6">
                <h3 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
                  Premium
                  <Crown className="w-5 h-5 text-gold-500" />
                </h3>
                <p className="text-zinc-400 text-sm">Expérience sans publicité ni démarchage</p>
              </div>

              <div className="mb-6">
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-bold text-white">9,99 €</span>
                  <span className="text-zinc-400">/ mois</span>
                </div>
                <p className="text-emerald-400 text-sm mt-1">Sans engagement</p>
              </div>

              <ul className="space-y-3 mb-6">
                {premiumFeatures.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <CheckCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${feature.highlight ? 'text-gold-500' : 'text-emerald-500'}`} />
                    <span className={`text-sm ${feature.highlight ? 'text-white font-medium' : 'text-zinc-300'}`}>
                      {feature.text}
                    </span>
                  </li>
                ))}
              </ul>

              <button
                onClick={onGetStarted}
                className="w-full py-3 px-6 rounded-xl bg-gradient-to-r from-gold-500 to-amber-500 hover:from-gold-400 hover:to-amber-400 text-black font-bold transition-all duration-300 shadow-lg shadow-gold-500/25 hover:shadow-gold-500/40"
                data-testid="pricing-premium-btn"
              >
                Passer en Premium
              </button>

              {/* Reassurance text */}
              <div className="mt-6 pt-6 border-t border-zinc-700">
                <p className="text-zinc-400 text-xs leading-relaxed text-center">
                  Un seul devis mal analysé peut coûter plusieurs milliers d'euros.
                  <br />
                  <span className="text-gold-400">Assistant by Léon vous aide à vérifier avant de payer.</span>
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </Section>
  );
}

// ==================== INTERACTIVE DEMO SECTION ====================
function InteractiveDemoSection({ onGetStarted }) {
  const [activeStep, setActiveStep] = React.useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = React.useState(true);

  const demoSteps = [
    {
      id: 0,
      title: "Importez votre devis",
      description: "Glissez votre fichier PDF ou prenez une photo de votre devis papier.",
      mockUI: "upload"
    },
    {
      id: 1,
      title: "Léon analyse en quelques secondes",
      description: "L'IA vérifie les mentions légales, les prix et la cohérence technique.",
      mockUI: "analyzing"
    },
    {
      id: 2,
      title: "Découvrez les alertes",
      description: "Léon détecte les incohérences, oublis et prix anormaux.",
      mockUI: "alerts"
    },
    {
      id: 3,
      title: "Obtenez votre rapport",
      description: "Un score de fiabilité et des recommandations claires avant de payer.",
      mockUI: "report"
    }
  ];

  // Auto-play animation
  React.useEffect(() => {
    if (!isAutoPlaying) return;
    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % demoSteps.length);
    }, 3500);
    return () => clearInterval(interval);
  }, [isAutoPlaying, demoSteps.length]);

  // Mock UI Components
  const MockUploadUI = () => (
    <motion.div 
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="bg-zinc-900 rounded-xl p-6 border border-zinc-700"
    >
      <div className="border-2 border-dashed border-zinc-600 rounded-xl p-8 text-center hover:border-gold-500/50 transition-colors">
        <motion.div
          animate={{ y: [0, -8, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="w-16 h-16 mx-auto mb-4 bg-gold-500/20 rounded-full flex items-center justify-center"
        >
          <FileText className="w-8 h-8 text-gold-500" />
        </motion.div>
        <p className="text-white font-medium mb-1">Importer votre devis</p>
        <p className="text-zinc-500 text-sm">PDF, Image ou Photo</p>
        <motion.div 
          initial={{ width: 0 }}
          animate={{ width: "60%" }}
          transition={{ duration: 1.5, delay: 0.5 }}
          className="h-1 bg-gold-500 rounded-full mx-auto mt-4"
        />
      </div>
    </motion.div>
  );

  const MockAnalyzingUI = () => (
    <motion.div 
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="bg-zinc-900 rounded-xl p-6 border border-zinc-700"
    >
      <div className="flex items-center gap-4 mb-6">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          className="w-12 h-12 border-4 border-gold-500 border-t-transparent rounded-full"
        />
        <div>
          <p className="text-white font-medium">Léon analyse votre devis...</p>
          <p className="text-zinc-500 text-sm">Vérification en cours</p>
        </div>
      </div>
      
      {[
        { label: "Mentions légales", progress: 100 },
        { label: "Cohérence technique", progress: 75 },
        { label: "Analyse des prix", progress: 45 }
      ].map((item, idx) => (
        <div key={idx} className="mb-3">
          <div className="flex justify-between text-sm mb-1">
            <span className="text-zinc-400">{item.label}</span>
            <span className="text-gold-500">{item.progress}%</span>
          </div>
          <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${item.progress}%` }}
              transition={{ duration: 1.5, delay: idx * 0.3 }}
              className="h-full bg-gradient-to-r from-gold-500 to-amber-500 rounded-full"
            />
          </div>
        </div>
      ))}
    </motion.div>
  );

  const MockAlertsUI = () => (
    <motion.div 
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="bg-zinc-900 rounded-xl p-6 border border-zinc-700"
    >
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 bg-red-500/20 rounded-full flex items-center justify-center">
          <AlertTriangle className="w-5 h-5 text-red-400" />
        </div>
        <div>
          <p className="text-white font-medium">3 alertes détectées</p>
          <p className="text-zinc-500 text-sm">Risque de surcoût estimé</p>
        </div>
      </div>
      
      <div className="space-y-2">
        {[
          { icon: XCircle, text: "Assurance décennale manquante", color: "red" },
          { icon: AlertTriangle, text: "Prix peinture +35% vs marché", color: "orange" },
          { icon: AlertTriangle, text: "Conditions de paiement floues", color: "orange" }
        ].map((alert, idx) => (
          <motion.div
            key={idx}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: idx * 0.2 }}
            className={`flex items-center gap-3 p-3 rounded-lg ${
              alert.color === "red" ? "bg-red-500/10 border border-red-500/30" : "bg-orange-500/10 border border-orange-500/30"
            }`}
          >
            <alert.icon className={`w-4 h-4 ${alert.color === "red" ? "text-red-400" : "text-orange-400"}`} />
            <span className="text-sm text-zinc-300">{alert.text}</span>
          </motion.div>
        ))}
      </div>
      
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8 }}
        className="mt-4 p-3 bg-red-500/10 rounded-lg border border-red-500/20"
      >
        <p className="text-red-400 font-bold text-lg">350 € - 1 100 €</p>
        <p className="text-zinc-500 text-xs">Risque de surcoût estimé</p>
      </motion.div>
    </motion.div>
  );

  const MockReportUI = () => (
    <motion.div 
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="bg-zinc-900 rounded-xl p-6 border border-zinc-700"
    >
      <div className="text-center mb-6">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 200, delay: 0.2 }}
          className="w-20 h-20 mx-auto mb-3 bg-gradient-to-br from-red-500/20 to-orange-500/20 rounded-full flex items-center justify-center border-4 border-red-500/30"
        >
          <span className="text-3xl font-bold text-red-400">30</span>
        </motion.div>
        <p className="text-white font-medium">Score de fiabilité</p>
        <span className="inline-block mt-1 px-3 py-1 bg-red-500/20 text-red-400 text-xs font-bold rounded-full">
          Devis risqué
        </span>
      </div>
      
      <div className="grid grid-cols-3 gap-2 mb-4">
        {[
          { label: "Admin", value: "30%", color: "red" },
          { label: "Tech", value: "40%", color: "orange" },
          { label: "Prix", value: "30%", color: "red" }
        ].map((item, idx) => (
          <div key={idx} className="text-center p-2 bg-zinc-800 rounded-lg">
            <p className={`text-lg font-bold ${item.color === "red" ? "text-red-400" : "text-orange-400"}`}>
              {item.value}
            </p>
            <p className="text-xs text-zinc-500">{item.label}</p>
          </div>
        ))}
      </div>
      
      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        className="w-full py-3 bg-gradient-to-r from-gold-500 to-amber-500 text-black font-bold rounded-lg flex items-center justify-center gap-2"
      >
        <FileText className="w-4 h-4" />
        Télécharger le rapport PDF
      </motion.button>
    </motion.div>
  );

  const renderMockUI = () => {
    switch (demoSteps[activeStep].mockUI) {
      case "upload": return <MockUploadUI />;
      case "analyzing": return <MockAnalyzingUI />;
      case "alerts": return <MockAlertsUI />;
      case "report": return <MockReportUI />;
      default: return null;
    }
  };

  return (
    <Section dark id="demo-interactive">
      <SectionTitle 
        overline="Comment ça marche"
        title="Analysez un devis en 30 secondes"
        subtitle="Avant de payer un artisan, vérifiez que son devis est fiable."
      />

      <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
        {/* Left: Steps */}
        <div className="space-y-4">
          {demoSteps.map((step, idx) => (
            <motion.div
              key={step.id}
              initial={{ opacity: 0.5 }}
              animate={{ 
                opacity: activeStep === idx ? 1 : 0.5,
                scale: activeStep === idx ? 1 : 0.98
              }}
              onClick={() => {
                setActiveStep(idx);
                setIsAutoPlaying(false);
              }}
              className={`p-4 rounded-xl cursor-pointer transition-all duration-300 ${
                activeStep === idx 
                  ? "bg-gradient-to-r from-gold-500/20 to-amber-500/10 border border-gold-500/50" 
                  : "bg-zinc-800/30 border border-zinc-700/50 hover:border-zinc-600"
              }`}
            >
              <div className="flex items-start gap-4">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                  activeStep === idx ? "bg-gold-500 text-black" : "bg-zinc-700 text-zinc-400"
                }`}>
                  <span className="font-bold">{idx + 1}</span>
                </div>
                <div>
                  <h3 className={`font-bold mb-1 ${activeStep === idx ? "text-white" : "text-zinc-400"}`}>
                    {step.title}
                  </h3>
                  <p className={`text-sm ${activeStep === idx ? "text-zinc-300" : "text-zinc-500"}`}>
                    {step.description}
                  </p>
                </div>
              </div>
              
              {/* Progress bar for active step */}
              {activeStep === idx && isAutoPlaying && (
                <motion.div 
                  initial={{ width: 0 }}
                  animate={{ width: "100%" }}
                  transition={{ duration: 3.5, ease: "linear" }}
                  className="h-0.5 bg-gold-500 rounded-full mt-3"
                />
              )}
            </motion.div>
          ))}
          
          {/* CTA */}
          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="pt-4"
          >
            <CTAButton onClick={onGetStarted} icon={Sparkles}>
              Essayer gratuitement
            </CTAButton>
            <p className="text-zinc-500 text-xs mt-2 text-center lg:text-left">
              Aucune carte bancaire requise
            </p>
          </motion.div>
        </div>

        {/* Right: Mock UI */}
        <div className="relative">
          {/* Glow effect */}
          <div className="absolute -inset-4 bg-gradient-to-r from-gold-500/10 via-amber-500/5 to-gold-500/10 rounded-3xl blur-2xl" />
          
          {/* Phone/Browser frame */}
          <div className="relative bg-zinc-800 rounded-2xl p-2 border border-zinc-700 shadow-2xl">
            {/* Browser bar */}
            <div className="flex items-center gap-2 px-3 py-2 bg-zinc-900 rounded-t-xl border-b border-zinc-700">
              <div className="flex gap-1.5">
                <div className="w-3 h-3 rounded-full bg-red-500/80" />
                <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
                <div className="w-3 h-3 rounded-full bg-green-500/80" />
              </div>
              <div className="flex-1 mx-4">
                <div className="bg-zinc-800 rounded-md px-3 py-1 text-xs text-zinc-500 flex items-center gap-2">
                  <Lock className="w-3 h-3" />
                  assistantbyleon.fr
                </div>
              </div>
            </div>
            
            {/* Content */}
            <div className="p-4 min-h-[350px]">
              {renderMockUI()}
            </div>
          </div>
          
          {/* Léon mascot */}
          <motion.div
            animate={{ y: [0, -5, 0] }}
            transition={{ duration: 3, repeat: Infinity }}
            className="absolute -bottom-6 -right-6 z-10"
          >
            <div className="relative">
              <div className="w-20 h-20 bg-gradient-to-br from-gold-500 to-amber-600 rounded-full flex items-center justify-center shadow-lg shadow-gold-500/30">
                <span className="text-3xl">👷</span>
              </div>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.5 }}
                className="absolute -top-2 -right-2 bg-gold-500 text-black text-xs font-bold px-2 py-1 rounded-full"
              >
                Léon
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
      
      {/* Bottom message */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="text-center mt-12 max-w-2xl mx-auto"
      >
        <p className="text-zinc-400 text-lg">
          <span className="text-gold-400 font-semibold">Travaux, devis, questions techniques…</span>
          <br />Léon vous aide à prendre les bonnes décisions.
        </p>
        <p className="text-zinc-500 text-sm mt-2">
          assistantbyleon.fr
        </p>
      </motion.div>
    </Section>
  );
}

export default function Home() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleGetStarted = () => {
    if (user) {
      navigate('/dashboard');
    } else {
      navigate('/auth');
    }
  };

  const handleAnalyzeQuote = () => {
    if (user) {
      navigate('/audit');
    } else {
      navigate('/auth');
    }
  };

  return (
    <div className="min-h-screen bg-black">
      {/* ==================== HERO SECTION - PREMIUM ==================== */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden" style={{ backgroundColor: '#000000' }}>
        <div className="absolute inset-0 bg-black" />
        <div className="absolute right-0 top-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-radial from-zinc-900/50 via-transparent to-transparent opacity-50" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            
            {/* ===== LEFT SIDE: Text + CTAs ===== */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center lg:text-left order-2 lg:order-1"
            >
              {/* Main Title */}
              <h1 className="text-4xl sm:text-5xl lg:text-[3.5rem] xl:text-6xl font-heading font-bold text-white leading-[1.1] mb-6">
                Le copilote intelligent{' '}
                <span className="bg-gradient-to-r from-gold-400 to-amber-400 bg-clip-text text-transparent">
                  de vos travaux.
                </span>
              </h1>

              {/* Subtitle */}
              <motion.p 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="text-lg md:text-xl text-zinc-300 mb-8 leading-relaxed max-w-xl mx-auto lg:mx-0"
              >
                <span className="text-gold-400 font-semibold">Léon</span> vous accompagne pendant tous vos travaux :{' '}
                <span className="text-white font-semibold">analyse de devis</span>,{' '}
                <span className="text-white font-semibold">diagnostic photo</span>,{' '}
                recettes travaux et assistant IA.
              </motion.p>

              {/* CTA Buttons - UPDATED */}
              <motion.div 
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-8"
              >
                <Button
                  onClick={handleGetStarted}
                  className="bg-gradient-to-r from-gold-500 to-amber-500 hover:from-gold-400 hover:to-amber-400 text-black font-bold px-8 py-6 text-base md:text-lg rounded-xl shadow-lg shadow-gold-500/25 hover:shadow-gold-500/40 hover:scale-[1.02] transition-all duration-300 group"
                  data-testid="hero-cta-primary"
                >
                  <Zap className="w-5 h-5 mr-2" />
                  Essayer gratuitement
                </Button>
                <Button
                  onClick={() => navigate('/comment-ca-marche')}
                  variant="outline"
                  className="border-2 border-zinc-600 hover:border-gold-500/50 text-zinc-300 hover:text-gold-400 hover:bg-gold-500/5 px-8 py-6 text-base md:text-lg rounded-xl transition-all duration-300"
                  data-testid="hero-cta-secondary"
                >
                  <Eye className="w-5 h-5 mr-2" />
                  Découvrir comment ça marche
                </Button>
              </motion.div>

              {/* Trust Proof - UPDATED */}
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
                className="flex flex-wrap items-center justify-center lg:justify-start gap-x-5 gap-y-2 text-sm text-zinc-400"
              >
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                  <span>Gratuit pour commencer</span>
                </div>
                <div className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-gold-500" />
                  <span>Analyse en quelques secondes</span>
                </div>
                <div className="flex items-center gap-2">
                  <Lock className="w-4 h-4 text-blue-500" />
                  <span>Données sécurisées</span>
                </div>
              </motion.div>
            </motion.div>

            {/* ===== RIGHT SIDE: Leon + Animated Cards ===== */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6 }}
              className="relative order-1 lg:order-2 flex items-center justify-center min-h-[420px] lg:min-h-[500px]"
            >
              <div className="relative w-[320px] h-[320px] md:w-[380px] md:h-[380px] lg:w-[420px] lg:h-[420px]">
                
                {/* LÉON - ANIMATION TRÈS VISIBLE DE SALUT */}
                <motion.div 
                  className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10"
                  data-testid="leon-hero-waving"
                >
                  <motion.div
                    animate={{ 
                      y: [0, -8, 0],
                      rotate: [-3, 3, -3],
                    }}
                    transition={{ 
                      y: { duration: 0.8, repeat: Infinity, ease: "easeInOut" },
                      rotate: { duration: 1.2, repeat: Infinity, ease: "easeInOut" },
                    }}
                  >
                    <motion.img 
                      src={LEON_HERO}
                      alt="Léon - Votre conducteur de travaux IA"
                      className="w-40 h-40 md:w-48 md:h-48 lg:w-56 lg:h-56 object-contain"
                      animate={{ scale: [1, 1.05, 1] }}
                      transition={{ scale: { duration: 1.5, repeat: Infinity, ease: "easeInOut" } }}
                    />
                  </motion.div>
                  
                  {/* Bras qui salue */}
                  <motion.div
                    className="absolute -right-2 top-8 md:top-10 w-12 h-12 md:w-14 md:h-14"
                    animate={{
                      rotate: [0, -25, 0, -25, 0],
                      x: [0, 5, 0, 5, 0],
                      y: [0, -8, 0, -8, 0],
                    }}
                    transition={{ duration: 1.2, repeat: Infinity, ease: "easeInOut" }}
                  >
                    <div className="text-4xl md:text-5xl">👋</div>
                  </motion.div>
                  
                  {/* Effet de brillance */}
                  <motion.div
                    className="absolute inset-0 -m-4 rounded-full"
                    animate={{
                      boxShadow: [
                        "0 0 20px rgba(212,175,55,0.2)",
                        "0 0 40px rgba(212,175,55,0.4)",
                        "0 0 20px rgba(212,175,55,0.2)",
                      ]
                    }}
                    transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                  />
                </motion.div>

                {/* ===== 8 CARTES AVEC MESSAGES CLAIRS ===== */}
                
                {/* Card 1: Devis analysé - Position 12h */}
                <motion.div 
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ 
                    opacity: 1, scale: 1,
                    y: [0, -12, 0], x: [0, 5, 0], rotate: [-5, 5, -5],
                  }}
                  transition={{ 
                    opacity: { duration: 0.3, delay: 0.1 },
                    scale: { duration: 0.3, delay: 0.1 },
                    y: { duration: 1.5, repeat: Infinity, ease: "easeInOut" },
                    x: { duration: 2, repeat: Infinity, ease: "easeInOut" },
                    rotate: { duration: 1.8, repeat: Infinity, ease: "easeInOut" }
                  }}
                  whileHover={{ scale: 1.25, zIndex: 50 }}
                  className="absolute left-1/2 -translate-x-1/2 -top-2 bg-black/90 backdrop-blur-sm border border-emerald-500/60 rounded-xl p-2.5 z-20 cursor-pointer shadow-lg shadow-emerald-500/20"
                >
                  <div className="flex items-center gap-2">
                    <motion.div 
                      animate={{ rotate: [0, 360] }}
                      transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                      className="w-7 h-7 rounded-lg bg-emerald-500/30 flex items-center justify-center"
                    >
                      <FileCheck className="w-4 h-4 text-emerald-400" />
                    </motion.div>
                    <div>
                      <p className="text-white font-bold text-xs">Devis analysé</p>
                      <p className="text-emerald-400 text-[10px] font-medium">Conforme ✓</p>
                    </div>
                  </div>
                </motion.div>

                {/* Card 2: Poste manquant détecté - Position 1h30 */}
                <motion.div 
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ 
                    opacity: 1, scale: 1,
                    y: [-5, 8, -5], x: [0, -6, 0], rotate: [3, -3, 3],
                  }}
                  transition={{ 
                    opacity: { duration: 0.3, delay: 0.15 },
                    scale: { duration: 0.3, delay: 0.15 },
                    y: { duration: 1.3, repeat: Infinity, ease: "easeInOut" },
                    x: { duration: 1.8, repeat: Infinity, ease: "easeInOut" },
                    rotate: { duration: 1.6, repeat: Infinity, ease: "easeInOut" }
                  }}
                  whileHover={{ scale: 1.25, zIndex: 50 }}
                  className="absolute right-0 top-[15%] bg-black/90 backdrop-blur-sm border border-orange-500/60 rounded-xl p-2.5 z-20 cursor-pointer shadow-lg shadow-orange-500/20"
                >
                  <div className="flex items-center gap-2">
                    <motion.div 
                      animate={{ scale: [1, 1.4, 1] }}
                      transition={{ duration: 0.5, repeat: Infinity }}
                      className="w-7 h-7 rounded-lg bg-orange-500/30 flex items-center justify-center"
                    >
                      <AlertTriangle className="w-4 h-4 text-orange-400" />
                    </motion.div>
                    <div>
                      <p className="text-white font-bold text-xs">Poste manquant</p>
                      <p className="text-orange-400 text-[10px] font-medium">Détecté !</p>
                    </div>
                  </div>
                </motion.div>

                {/* Card 3: Photo analysée - Position 3h */}
                <motion.div 
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ 
                    opacity: 1, scale: 1,
                    y: [0, -10, 0], x: [-3, 3, -3], rotate: [-4, 4, -4],
                  }}
                  transition={{ 
                    opacity: { duration: 0.3, delay: 0.2 },
                    scale: { duration: 0.3, delay: 0.2 },
                    y: { duration: 1.7, repeat: Infinity, ease: "easeInOut" },
                    x: { duration: 1.4, repeat: Infinity, ease: "easeInOut" },
                    rotate: { duration: 2, repeat: Infinity, ease: "easeInOut" }
                  }}
                  whileHover={{ scale: 1.25, zIndex: 50 }}
                  className="absolute -right-2 top-1/2 -translate-y-1/2 bg-black/90 backdrop-blur-sm border border-blue-500/60 rounded-xl p-2.5 z-20 cursor-pointer shadow-lg shadow-blue-500/20"
                >
                  <div className="flex items-center gap-2">
                    <motion.div 
                      animate={{ scale: [1, 1.2, 1], rotate: [0, 10, 0] }}
                      transition={{ duration: 1.2, repeat: Infinity }}
                      className="w-7 h-7 rounded-lg bg-blue-500/30 flex items-center justify-center"
                    >
                      <Camera className="w-4 h-4 text-blue-400" />
                    </motion.div>
                    <div>
                      <p className="text-white font-bold text-xs">Diagnostic photo</p>
                      <p className="text-blue-400 text-[10px] font-medium">Analysée ✓</p>
                    </div>
                  </div>
                </motion.div>

                {/* Card 4: Étape terminée - Position 4h30 */}
                <motion.div 
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ 
                    opacity: 1, scale: 1,
                    y: [5, -8, 5], x: [0, 5, 0], rotate: [2, -4, 2],
                  }}
                  transition={{ 
                    opacity: { duration: 0.3, delay: 0.25 },
                    scale: { duration: 0.3, delay: 0.25 },
                    y: { duration: 1.4, repeat: Infinity, ease: "easeInOut" },
                    x: { duration: 2.1, repeat: Infinity, ease: "easeInOut" },
                    rotate: { duration: 1.9, repeat: Infinity, ease: "easeInOut" }
                  }}
                  whileHover={{ scale: 1.25, zIndex: 50 }}
                  className="absolute right-[5%] bottom-[18%] bg-black/90 backdrop-blur-sm border border-indigo-500/60 rounded-xl p-2.5 z-20 cursor-pointer shadow-lg shadow-indigo-500/20"
                >
                  <div className="flex items-center gap-2">
                    <motion.div
                      animate={{ rotate: [0, -15, 0, 15, 0] }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                      className="w-7 h-7 rounded-lg bg-indigo-500/30 flex items-center justify-center"
                    >
                      <Clock className="w-4 h-4 text-indigo-400" />
                    </motion.div>
                    <div>
                      <p className="text-white font-bold text-xs">Étape terminée</p>
                      <p className="text-indigo-400 text-[10px] font-medium">Placo OK</p>
                    </div>
                  </div>
                </motion.div>

                {/* Card 5: Économie détectée - STAR CARD - Position 6h */}
                <motion.div 
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1, y: [-8, 8, -8] }}
                  transition={{ 
                    opacity: { duration: 0.4, delay: 0.3 },
                    scale: { duration: 0.4, delay: 0.3, type: "spring", bounce: 0.5 },
                    y: { duration: 1.2, repeat: Infinity, ease: "easeInOut" }
                  }}
                  whileHover={{ scale: 1.3, zIndex: 50 }}
                  className="absolute left-1/2 -translate-x-1/2 -bottom-3 z-30 cursor-pointer"
                >
                  <motion.div 
                    animate={{ 
                      rotate: [2, -2, 2],
                      boxShadow: [
                        "0 0 15px rgba(212,175,55,0.4)",
                        "0 0 35px rgba(212,175,55,0.7)",
                        "0 0 15px rgba(212,175,55,0.4)"
                      ]
                    }}
                    transition={{ 
                      rotate: { duration: 1, repeat: Infinity, ease: "easeInOut" },
                      boxShadow: { duration: 0.8, repeat: Infinity, ease: "easeInOut" }
                    }}
                    className="bg-black/95 backdrop-blur-sm border-2 border-gold-500 rounded-xl p-3"
                  >
                    <div className="flex items-center gap-2.5">
                      <motion.div 
                        animate={{ rotate: [0, 25, -25, 0], scale: [1, 1.3, 1] }}
                        transition={{ duration: 0.6, repeat: Infinity }}
                        className="w-9 h-9 rounded-lg bg-gold-500/40 flex items-center justify-center"
                      >
                        <PiggyBank className="w-5 h-5 text-gold-400" />
                      </motion.div>
                      <div>
                        <p className="text-white font-bold text-sm">Économie détectée</p>
                        <motion.p 
                          animate={{ scale: [1, 1.15, 1] }}
                          transition={{ duration: 0.6, repeat: Infinity }}
                          className="text-gold-400 text-lg font-black"
                        >
                          2 450 €
                        </motion.p>
                      </div>
                    </div>
                  </motion.div>
                </motion.div>

                {/* Card 6: Budget estimé - Position 7h30 */}
                <motion.div 
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ 
                    opacity: 1, scale: 1,
                    y: [0, 10, 0], x: [3, -3, 3], rotate: [-3, 3, -3],
                  }}
                  transition={{ 
                    opacity: { duration: 0.3, delay: 0.35 },
                    scale: { duration: 0.3, delay: 0.35 },
                    y: { duration: 1.6, repeat: Infinity, ease: "easeInOut" },
                    x: { duration: 1.3, repeat: Infinity, ease: "easeInOut" },
                    rotate: { duration: 1.7, repeat: Infinity, ease: "easeInOut" }
                  }}
                  whileHover={{ scale: 1.25, zIndex: 50 }}
                  className="absolute left-[5%] bottom-[18%] bg-black/90 backdrop-blur-sm border border-purple-500/60 rounded-xl p-2.5 z-20 cursor-pointer shadow-lg shadow-purple-500/20"
                >
                  <div className="flex items-center gap-2">
                    <motion.div
                      animate={{ y: [0, -3, 0] }}
                      transition={{ duration: 0.8, repeat: Infinity }}
                      className="w-7 h-7 rounded-lg bg-purple-500/30 flex items-center justify-center"
                    >
                      <Calculator className="w-4 h-4 text-purple-400" />
                    </motion.div>
                    <div>
                      <p className="text-white font-bold text-xs">Budget estimé</p>
                      <p className="text-purple-400 text-[10px] font-medium">12 500€</p>
                    </div>
                  </div>
                </motion.div>

                {/* Card 7: Facture vérifiée - Position 9h */}
                <motion.div 
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ 
                    opacity: 1, scale: 1,
                    y: [-6, 8, -6], x: [2, -4, 2], rotate: [4, -2, 4],
                  }}
                  transition={{ 
                    opacity: { duration: 0.3, delay: 0.4 },
                    scale: { duration: 0.3, delay: 0.4 },
                    y: { duration: 1.5, repeat: Infinity, ease: "easeInOut" },
                    x: { duration: 1.9, repeat: Infinity, ease: "easeInOut" },
                    rotate: { duration: 1.4, repeat: Infinity, ease: "easeInOut" }
                  }}
                  whileHover={{ scale: 1.25, zIndex: 50 }}
                  className="absolute -left-2 top-1/2 -translate-y-1/2 bg-black/90 backdrop-blur-sm border border-cyan-500/60 rounded-xl p-2.5 z-20 cursor-pointer shadow-lg shadow-cyan-500/20"
                >
                  <div className="flex items-center gap-2">
                    <motion.div
                      animate={{ scale: [1, 1.15, 1] }}
                      transition={{ duration: 1, repeat: Infinity }}
                      className="w-7 h-7 rounded-lg bg-cyan-500/30 flex items-center justify-center"
                    >
                      <Receipt className="w-4 h-4 text-cyan-400" />
                    </motion.div>
                    <div>
                      <p className="text-white font-bold text-xs">Facture vérifiée</p>
                      <p className="text-cyan-400 text-[10px] font-medium">Validée ✓</p>
                    </div>
                  </div>
                </motion.div>

                {/* Card 8: Conseil de Léon - Position 10h30 */}
                <motion.div 
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ 
                    opacity: 1, scale: 1,
                    y: [5, -10, 5], x: [-2, 4, -2], rotate: [-2, 5, -2],
                  }}
                  transition={{ 
                    opacity: { duration: 0.3, delay: 0.45 },
                    scale: { duration: 0.3, delay: 0.45 },
                    y: { duration: 1.8, repeat: Infinity, ease: "easeInOut" },
                    x: { duration: 1.5, repeat: Infinity, ease: "easeInOut" },
                    rotate: { duration: 2.2, repeat: Infinity, ease: "easeInOut" }
                  }}
                  whileHover={{ scale: 1.25, zIndex: 50 }}
                  className="absolute left-0 top-[15%] bg-black/90 backdrop-blur-sm border border-amber-500/60 rounded-xl p-2.5 z-20 cursor-pointer shadow-lg shadow-amber-500/20"
                >
                  <div className="flex items-center gap-2">
                    <motion.div 
                      animate={{ scale: [1, 1.2, 1], rotate: [0, -10, 0] }}
                      transition={{ duration: 0.9, repeat: Infinity }}
                      className="w-7 h-7 rounded-lg bg-amber-500/30 flex items-center justify-center"
                    >
                      <Lightbulb className="w-4 h-4 text-amber-400" />
                    </motion.div>
                    <div>
                      <p className="text-white font-bold text-xs">Conseil Léon</p>
                      <p className="text-amber-400 text-[10px] font-medium">Nouveau !</p>
                    </div>
                  </div>
                </motion.div>

              </div>
            </motion.div>
          </div>
        </div>

        {/* Scroll indicator */}
        <motion.div 
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.8, repeat: Infinity }}
          className="absolute bottom-6 left-1/2 -translate-x-1/2 hidden md:block"
        >
          <div className="w-6 h-10 border-2 border-zinc-700 rounded-full flex items-start justify-center p-2">
            <motion.div 
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="w-1.5 h-1.5 bg-gold-500 rounded-full"
            />
          </div>
        </motion.div>
      </section>

      {/* ==================== PHRASE DE VALEUR ==================== */}
      <div className="bg-zinc-900/50 border-y border-zinc-800">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto px-4 py-8 text-center"
        >
          <p className="text-xl md:text-2xl text-zinc-300 leading-relaxed">
            Travaux, devis, questions techniques…
            <br />
            <span className="text-gold-400 font-semibold">Léon vous aide à prendre les bonnes décisions.</span>
          </p>
        </motion.div>
      </div>

      {/* ==================== LES 4 PILIERS DE L'APPLICATION ==================== */}
      <Section id="fonctionnalites">
        <SectionTitle 
          overline="Fonctionnalités"
          title="Les super pouvoirs de Léon"
          subtitle="Les 4 piliers pour gérer vos travaux sereinement"
        />

        <motion.div 
          variants={staggerContainer}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          className="grid md:grid-cols-2 gap-6 lg:gap-8"
        >
          {leonPowers.map((power, idx) => (
            <motion.div
              key={idx}
              variants={scaleIn}
              whileHover={{ y: -5, transition: { duration: 0.3 } }}
              className="group cursor-pointer"
              onClick={() => navigate(power.link)}
            >
              {/* Card with detailed content */}
              <div className="relative bg-black rounded-2xl p-6 lg:p-8 border border-zinc-800 hover:border-gold-500/50 transition-all duration-300 h-full overflow-hidden">
                {/* Gold gradient on hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-gold-500/5 to-amber-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                
                <div className="relative">
                  {/* Header: Icon + Title */}
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-gold-500 to-amber-500 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                      <power.icon className="w-7 h-7 text-black" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl lg:text-2xl font-bold text-white group-hover:text-gold-400 transition-colors">
                        {power.title}
                      </h3>
                      <p className="text-zinc-400 mt-1 text-sm lg:text-base leading-relaxed">
                        {power.description}
                      </p>
                    </div>
                  </div>

                  {/* Details intro */}
                  {power.detailsIntro && (
                    <p className="text-zinc-300 text-sm mb-3">{power.detailsIntro}</p>
                  )}
                  {!power.detailsIntro && power.details && (
                    <p className="text-zinc-300 text-sm mb-3">Léon vous permet de :</p>
                  )}

                  {/* Bullet points */}
                  <ul className="space-y-2 mb-4">
                    {power.details.map((detail, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <CheckCircle className="w-4 h-4 text-gold-500 flex-shrink-0 mt-0.5" />
                        <span className="text-zinc-400 group-hover:text-zinc-300 transition-colors">{detail}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Conclusion */}
                  <p className="text-zinc-300 text-sm italic border-l-2 border-gold-500/50 pl-3">
                    {power.conclusion}
                  </p>

                  {/* CTA Arrow */}
                  <div className="flex items-center justify-end mt-4 pt-4 border-t border-zinc-800/50">
                    <span className="text-gold-500 text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                      Découvrir
                      <ChevronRight className="w-4 h-4" />
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        <motion.div 
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <CTAButton onClick={handleGetStarted} icon={Sparkles}>
            Commencer gratuitement
          </CTAButton>
        </motion.div>
      </Section>

      {/* ==================== SECTION DIAGNOSTIC PHOTO (SIMPLIFIÉE) ==================== */}
      <Section dark id="moment-wow">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto text-center"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500/10 border border-purple-500/20 rounded-full mb-6">
            <Camera className="w-5 h-5 text-purple-400" />
            <span className="text-purple-400 font-medium">Diagnostic instantané</span>
          </div>
          
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-white mb-4">
            Prenez une photo, Léon vous dit si c'est grave.
          </h2>
          
          <p className="text-lg text-zinc-400 max-w-2xl mx-auto mb-8">
            Une fissure ? Une tache d'humidité ? Envoyez une photo et recevez un diagnostic instantané avec des recommandations concrètes.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <CTAButton onClick={() => navigate('/fonctionnalite/diagnostic-photo')} icon={Camera}>
              Découvrir le diagnostic photo
            </CTAButton>
          </div>
        </motion.div>
      </Section>

      {/* ==================== ERREURS FRÉQUENTES - UPDATED ==================== */}
      <Section dark id="problemes">
        <SectionTitle 
          overline="On vous comprend"
          title="Les erreurs les plus fréquentes pendant des travaux"
          subtitle="Assistant by Léon résout ces problèmes"
        />

        <motion.div 
          variants={staggerContainer}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          className="grid md:grid-cols-3 gap-8"
        >
          {problems.map((problem, idx) => (
            <motion.div
              key={idx}
              variants={fadeInUp}
              className="group"
            >
              <div className="bg-zinc-800/50 rounded-2xl p-8 border border-zinc-700/50 hover:border-red-500/30 transition-all duration-300 h-full">
                <div className="mb-6">
                  <div className="w-14 h-14 rounded-xl bg-red-500/10 flex items-center justify-center mb-4 group-hover:bg-red-500/20 transition-colors">
                    <XCircle className="w-7 h-7 text-red-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">{problem.title}</h3>
                  <p className="text-zinc-400">{problem.description}</p>
                </div>

                <div className="flex justify-center my-4">
                  <ChevronRight className="w-6 h-6 text-gold-500 rotate-90" />
                </div>

                <div className="bg-emerald-500/10 rounded-xl p-4 border border-emerald-500/20">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <p className="text-emerald-300 text-sm">{problem.solution}</p>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        <motion.div 
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <CTAButton onClick={handleAnalyzeQuote} icon={FileSearch}>
            Analyser mon devis
          </CTAButton>
        </motion.div>
      </Section>

      {/* ==================== SECTION TARIFS ==================== */}
      <PricingSection onGetStarted={handleGetStarted} />

      {/* ==================== SECTION FINALE - UPDATED ==================== */}
      <Section id="cta-final" className="relative overflow-hidden">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-gold-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl" />

        <div className="relative max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="mb-8">
              <LeonAvatar size="xl" mood="happy" />
            </div>

            {/* UPDATED TITLE */}
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-6">
              Commencez vos travaux avec <span className="text-gold-500">Léon</span>
            </h2>

            {/* UPDATED SUBTITLE */}
            <p className="text-xl text-zinc-300 mb-10">
              L'assistant intelligent qui <span className="text-gold-400 font-semibold">protège vos travaux et votre argent</span>.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">
              <CTAButton onClick={handleGetStarted} icon={Zap} className="text-xl px-10 py-7">
                Tester Léon gratuitement
              </CTAButton>
            </div>

            {/* UPDATED TEXT */}
            <p className="text-zinc-400 text-sm mb-8">
              Gratuit pour commencer – aucun engagement.
            </p>

            <div className="flex flex-wrap items-center justify-center gap-8 text-zinc-500">
              <div className="flex items-center gap-2">
                <Lock className="w-5 h-5 text-blue-500" />
                <span className="text-sm">Données sécurisées</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-500" />
                <span className="text-sm">Conforme RGPD</span>
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-gold-500" />
                <span className="text-sm">IA de confiance</span>
              </div>
            </div>
          </motion.div>
        </div>
      </Section>

      <div className="h-20 bg-zinc-900" />
    </div>
  );
}
